import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import type { UseFormReturn } from 'react-hook-form';

import { marketplaceApi } from '../api/marketplaceApi';
import type { MarketplaceRootData } from '../schemas/rootSchema';
import { MarketplaceListParams } from '../types/types';
import { buildMarketplaceImageUploadItems } from '../utils/buildMarketplaceImageUploadItems';
import { buildMarketplacePayload } from '../utils/buildMarketplacePayload';
import { handleMarketplaceUploadFailures } from '../utils/handleMarketplaceUploadFailures';
import {
  type MarketplaceUploadImageItem,
  useMarketplaceImageUpload,
} from './useMarketplaceImageUpload';

interface RunMarketplaceImageUploadsParams {
  formData: MarketplaceRootData;
  marketplaceId: number;
  marketplaceFormMethods: UseFormReturn<MarketplaceRootData>;
  uploadMarketplaceImages: (payload: {
    marketplaceId: number;
    images: MarketplaceUploadImageItem[];
  }) => Promise<{
    failedUploads: { item: MarketplaceUploadImageItem; error: unknown }[];
  }>;
}

export async function runMarketplaceImageUploads({
  formData,
  marketplaceId,
  marketplaceFormMethods,
  uploadMarketplaceImages,
}: RunMarketplaceImageUploadsParams): Promise<void> {
  const uploadItems = buildMarketplaceImageUploadItems(formData);

  if (uploadItems.length === 0) return;

  const { failedUploads } = await uploadMarketplaceImages({
    marketplaceId,
    images: uploadItems,
  });

  if (failedUploads.length === 0) return;

  handleMarketplaceUploadFailures({
    failedUploads,
    notifyImageError: label => message.error(`Erro ao enviar ${label}.`),
    getProgramImages: () =>
      (marketplaceFormMethods.getValues(
        'content.programImages'
      ) as UploadFile[]) ?? [],
    setProgramImages: images => {
      marketplaceFormMethods.setValue('content.programImages', images, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    resetImageField: fieldName => {
      marketplaceFormMethods.setValue(
        fieldName as any,
        { preview: '', file: undefined },
        { shouldDirty: true, shouldValidate: true }
      );
    },
  });
}

interface CreateMarketplaceProductResult {
  marketplaceId: number;
}

export function useMarketplaceList(params: MarketplaceListParams) {
  const {
    data: response,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['marketplace-products', params],
    queryFn: () => marketplaceApi.fetchProducts(params),
    refetchOnWindowFocus: false,
  });

  return {
    items: response?.data ?? [],
    isLoading,
    total: response?.total ?? 0,
    refetch,
  };
}

export function useMarketplaceProduct(marketplaceId?: number) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['marketplace-product', marketplaceId],
    queryFn: () => marketplaceApi.fetchProductById(marketplaceId as number),
    enabled: !!marketplaceId,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  return {
    product: data,
    isLoading,
    refetch,
  };
}

export function useMarketplaceCreateProduct(
  marketplaceFormMethods: UseFormReturn<MarketplaceRootData>
) {
  const queryClient = useQueryClient();
  const { uploadMarketplaceImages, isUploading } = useMarketplaceImageUpload();

  const { mutateAsync: createMarketplaceProduct, isPending } = useMutation({
    mutationFn: async (
      formData: MarketplaceRootData
    ): Promise<CreateMarketplaceProductResult> => {
      const response = await marketplaceApi.createProduct(
        buildMarketplacePayload(formData)
      );

      await runMarketplaceImageUploads({
        formData,
        marketplaceId: response.id,
        marketplaceFormMethods,
        uploadMarketplaceImages,
      });

      return {
        marketplaceId: response.id,
      };
    },
    onSuccess: ({ marketplaceId }) => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-products'] });
      queryClient.invalidateQueries({
        queryKey: ['marketplace-product', marketplaceId],
      });
    },
  });

  return {
    createMarketplaceProduct,
    isCreatingProduct: isPending || isUploading,
  };
}

export function useMarketplaceUpdateProduct(
  marketplaceId: number,
  marketplaceFormMethods: UseFormReturn<MarketplaceRootData>
) {
  const queryClient = useQueryClient();
  const { uploadMarketplaceImages, isUploading } = useMarketplaceImageUpload();

  const { mutateAsync: updateMarketplaceProduct, isPending } = useMutation({
    mutationFn: async (formData: MarketplaceRootData): Promise<void> => {
      await marketplaceApi.updateProduct(
        marketplaceId,
        buildMarketplacePayload(formData)
      );

      await runMarketplaceImageUploads({
        formData,
        marketplaceId,
        marketplaceFormMethods,
        uploadMarketplaceImages,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['marketplace-product', marketplaceId],
      });
      queryClient.invalidateQueries({ queryKey: ['marketplace-products'] });
    },
  });

  return {
    updateMarketplaceProduct,
    isUpdatingProduct: isPending || isUploading,
  };
}

export function useMarketplaceUpdatePublishedProduct(
  marketplaceId: number,
  marketplaceFormMethods: UseFormReturn<MarketplaceRootData>
) {
  const queryClient = useQueryClient();
  const { uploadMarketplaceImages, isUploading } = useMarketplaceImageUpload();

  const { mutateAsync: updatePublishedProduct, isPending } = useMutation({
    mutationFn: async (formData: MarketplaceRootData): Promise<void> => {
      await marketplaceApi.updatePublishedProduct(
        marketplaceId,
        buildMarketplacePayload(formData)
      );

      await runMarketplaceImageUploads({
        formData,
        marketplaceId,
        marketplaceFormMethods,
        uploadMarketplaceImages,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['marketplace-product', marketplaceId],
      });
      queryClient.invalidateQueries({ queryKey: ['marketplace-products'] });
    },
  });

  return {
    updatePublishedProduct,
    isUpdatingPublishedProduct: isPending || isUploading,
  };
}

export function useMarketplaceActivateProduct(marketplaceId: number) {
  const queryClient = useQueryClient();

  const { mutateAsync: activateMarketplaceProduct, isPending } = useMutation({
    mutationFn: async (): Promise<void> => {
      await marketplaceApi.activateItem(marketplaceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['marketplace-product', marketplaceId],
      });
      queryClient.invalidateQueries({ queryKey: ['marketplace-products'] });
    },
  });

  return {
    activateMarketplaceProduct,
    isActivatingProduct: isPending,
  };
}
